import React, {
    cloneElement,
    isValidElement,
    ReactNode,
    Ref,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import ReactModal from 'react-modal';
import { CopyRoundedIcon } from '../assets/icons';
import useIsMounted from '../hooks/useIsMounted';
import useWidth from '../hooks/useWidth';

type CopyPopUpProp = {
    component?: ReactNode;
    className?: string;
    copyText?: string;
    text?: string;
};

type PositionObject = {
    top: string | number;
    left: string | number;
    right?: string | number;
};

const CopyPopUp: React.FC<CopyPopUpProp> = ({
    component,
    className = '',
    copyText = 'Copied!',
    text = '',
}) => {
    const iconRef = useRef<any>(null);
    const [contentRef, setContentRef] = useState<HTMLDivElement>();
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<PositionObject>({ top: 0, left: 0 });

    const windowWidth = useWidth();
    const isMounted = useIsMounted();

    const hide = () => {
        setOpen(false);
    };

    const handleClick: React.MouseEventHandler<SVGSVGElement> = () => {
        setOpen(true);
        navigator.clipboard.writeText(text);
    };

    const clonedIcon = isValidElement(component)
        ? cloneElement(component, {
              onClick: handleClick,
              ref: iconRef,
          })
        : undefined;

    const calculatePosition = useCallback(() => {
        if (!iconRef.current || !contentRef) return;
        const iconBounds = iconRef.current?.getBoundingClientRect();
        const contentBounds = contentRef?.getBoundingClientRect();

        if (!iconBounds || !contentBounds) return;

        const { top, left, right, height } = iconBounds;
        const { width } = contentBounds;

        const isOverflow = left + width > window.innerWidth;

        const topSpacing = height + 5;
        const margin = 6;

        setPosition((prev) => ({
            ...prev,
            top: top + topSpacing,
            left: !isOverflow ? left - margin : 'unset',
            right: isOverflow ? window.innerWidth - right - margin : 'unset',
        }));
    }, [iconRef, contentRef]);

    useEffect(() => {
        if (!open) return;

        calculatePosition();

        const scrollListener = () => {
            if (open) {
                hide();
            }
        };

        window.addEventListener('scroll', scrollListener);

        return () => window.removeEventListener('scroll', scrollListener);
    }, [calculatePosition, open, windowWidth]);

    useEffect(() => {
        if (open) {
            setTimeout(() => {
                isMounted() && hide();
            }, 3000);
        }
    }, [open, isMounted]);

    return (
        <div className='copy-container'>
            {clonedIcon ?? (
                <CopyRoundedIcon
                    onClick={handleClick}
                    ref={iconRef as Ref<SVGSVGElement>}
                />
            )}
            <ReactModal
                ariaHideApp={false}
                isOpen={open}
                overlayClassName='modal-overlay copy-pop-up'
                contentRef={(ref) => setContentRef(ref)}
                className={`modal-content ${className} ${
                    position?.right !== 'unset' ? 'flipped' : ''
                }`}
                style={{
                    content: {
                        top: position?.top,
                        left: position?.left,
                        right: position?.right,
                    },
                }}
                onRequestClose={hide}
            >
                <div className='content' onClick={(e) => e.stopPropagation()}>
                    {copyText}
                </div>
            </ReactModal>
        </div>
    );
};

export default CopyPopUp;
